import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure, TTableStructureFilter } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getNonEuExchangeRateListRequest } from 'redux/reducers/exchangeRates/reducer';
import {
	getNonEuExchangeRateList,
	getNonEuExchangeRateListLoading,
} from 'redux/reducers/exchangeRates/selectors';
import { getEmEnumsRequest } from 'redux/reducers/emManagement/reducer';
import { EEmEnums } from 'redux/reducers/emManagement/constants';
import { getEnumsCurrencyCode } from 'redux/reducers/emManagement/selectors';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { TUkExchangeRateRow } from './types';
import { withRowIds } from '../../utils';
import { EExchangeRatesTabGroupLabel } from '../../constants';

/** Columns whose filter options are the supported currency codes. */
const CURRENCY_FILTER_SELECTORS = ['fromCurrency', 'toCurrency'];

/** Served by the ExchangeRatesNonEu section of the API — POST /api/exchange_rates_non_eu. */
export const UkExchangeRates: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);

	const list = useSelector(getNonEuExchangeRateList);
	const listLoading = useSelector(getNonEuExchangeRateListLoading);
	const currencyCode = useSelector(getEnumsCurrencyCode);

	useEffect(() => {
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.CURRENCY_CODE }));
	}, [dispatch]);

	const params = useMemo(() => queryString.parse(search), [search]);

	const loadList = useCallback(() => {
		/**
		 * Every filter the table writes into the url is read back the same way the other sections do
		 * it: the separate filters from their `f-` params, the plain ones from their own, and the date
		 * columns expanded into the two bounds of the picked period. Reading from the default
		 * structure keeps the request out of reach of column visibility changes.
		 */
		const filters = getFiltersFromNavigateParams(defaultTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}

		dispatch(
			getNonEuExchangeRateListRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				orderBy,
				filters,
			}),
		);
	}, [dispatch, params]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	/** Currency codes the backend supports, offered as the options of the From Currency filter. */
	const currencyOptions = useMemo<TTableStructureFilter>(
		() =>
			(currencyCode ?? [])
				.filter((item) => item.value.length === 3)
				.map((item) => [item.value, item.displayName]),
		[currencyCode],
	);

	const structure = useMemo(
		() =>
			currentTableStructure.map((column) =>
				CURRENCY_FILTER_SELECTORS.includes(String(column.selector))
					? { ...column, filter: currencyOptions }
					: column,
			),
		[currencyOptions, currentTableStructure],
	);

	/** Keeps only the visibility flags, so the columns stay free of the derived filter options. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TUkExchangeRateRow>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const items = useMemo(() => withRowIds(list), [list]);

	return (
		<EasTableWithFilter<TUkExchangeRateRow>
			tableStructure={structure}
			items={items}
			itemsIsLoading={listLoading}
			handleAction={() => undefined}
			onTableStructureChange={handleTableStructureChange}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Typography variant="body1" color="primary">
					Resources / Reference Tables / Exchange Rates /{' '}
					<b>{EExchangeRatesTabGroupLabel.UK_EXCHANGE_RATES}</b>
				</Typography>
			</Box>
		</EasTableWithFilter>
	);
};
